/**
 * Cinelli Product Gallery v2
 * Initializes Swiper on [data-cin-gallery], binds thumbnails, handles
 * variant image filtering, counter, and PhotoSwipe integration.
 *
 * Listens to:
 *   - Document-level `variant:change` (dispatched by theme.Product)
 *   - Per-thumb click events
 *
 * Assumes window.Swiper is loaded (swiper-bundle.min.js).
 */
(function () {
  'use strict';

  function parseSetGroupFromAlt(alt) {
    if (!alt || typeof alt !== 'string' || alt.indexOf('#') === -1) return '';
    var afterHash = alt.split('#').pop();
    if (afterHash.indexOf('_') === -1) return '';
    return afterHash.split('_')[0];
  }

  // Return a Shopify CDN image URL constrained to the given width. Variant
  // JSON gives us the master src; we add `width` so the swapped slot image
  // isn't the full-resolution original.
  function withWidth(src, w) {
    if (!src) return src;
    if (src.indexOf('//') === 0) src = 'https:' + src;
    src = src.replace(/([?&])width=\d+/i, '$1').replace(/[?&]$/, '');
    return src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=' + w;
  }

  function CinGallery(rootEl) {
    this.root = rootEl;
    this.sectionId = rootEl.dataset.sectionId || '';
    this.mainEl = rootEl.querySelector('[data-cin-gallery-main]');
    this.thumbs = Array.prototype.slice.call(rootEl.querySelectorAll('[data-cin-gallery-thumb]'));
    this.counterCurrent = rootEl.querySelector('[data-cin-gallery-current]');
    this.counterTotal = rootEl.querySelector('[data-cin-gallery-total]');
    this.zoomBtn = rootEl.querySelector('[data-cin-gallery-zoom]');
    this.prevBtn = rootEl.querySelector('[data-cin-gallery-prev]');
    this.nextBtn = rootEl.querySelector('[data-cin-gallery-next]');
    this.dotsEl = rootEl.querySelector('[data-cin-gallery-dots]');
    this.initialSetName = rootEl.dataset.imageSet || '';

    // Variant-first mode: a dedicated slide/thumb at index 0 mirrors the
    // selected variant's image (variant images are kept out of the gallery).
    this.variantFirst = rootEl.dataset.variantFirst === 'true';
    this.variantSlot = rootEl.querySelector('[data-cin-gallery-variant-slot]');
    this.variantSlotThumb = rootEl.querySelector('[data-cin-gallery-variant-thumb]');
    this.variantSlotPswp = rootEl.querySelector('[data-cin-gallery-variant-pswp]');

    if (!this.mainEl || typeof window.Swiper !== 'function') {
      console.warn('[CinGallery] Swiper not loaded or main element missing');
      return;
    }

    this.initSwiper();
    this.bindThumbs();
    this.bindVariantChange();
    if (this.zoomBtn) this.bindImageZoom();
    if (this.initialSetName) {
      this.applyImageSetFilter(this.initialSetName);
      // BLOCKER #2 fix: after the initial image-set filter, seek to the
      // variant's featured media. Otherwise the swiper stays on slide 0,
      // which may belong to a different image-set and end up `is-hidden`.
      var featured = this.mainEl.querySelector('[data-is-featured="true"]');
      if (featured && featured.dataset.mediaId) {
        this.slideToMediaId(featured.dataset.mediaId);
      }
    }
    this.updateCounterTotal();
  }

  CinGallery.prototype.initSwiper = function () {
    var self = this;
    this.swiper = new window.Swiper(this.mainEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      watchOverflow: true,
      navigation: {
        prevEl: this.prevBtn,
        nextEl: this.nextBtn,
      },
      pagination: this.dotsEl ? {
        el: this.dotsEl,
        type: 'bullets',
        clickable: true,
      } : false,
      keyboard: { enabled: true },
      a11y: { enabled: true },
      on: {
        init: function () { self.onSlideChange(); },
        slideChange: function () { self.onSlideChange(); },
      },
    });
  };

  CinGallery.prototype.onSlideChange = function () {
    if (!this.swiper) return;
    var idx = this.swiper.realIndex;

    // BLOCKER #1 fix: counter shows "visible position / visible total",
    // not raw swiper index. Hidden slides (filtered by variant image-set)
    // are skipped in the count.
    var slides = this.mainEl.querySelectorAll('.swiper-slide');
    var visiblePos = 0;
    for (var i = 0; i < slides.length; i++) {
      if (i === idx) break;
      if (!slides[i].classList.contains('is-hidden')) visiblePos++;
    }
    var displayPos = visiblePos + 1;
    if (this.counterCurrent) this.counterCurrent.textContent = displayPos;

    // Active thumb compares absolute indexes (thumb.data-index and
    // slide.data-index are both 0-based positions in product.media).
    this.thumbs.forEach(function (t) {
      var thumbIdx = parseInt(t.dataset.index, 10);
      t.classList.toggle('is-active', thumbIdx === idx);
    });

    var active = this.root.querySelector('.cin-gallery__thumb.is-active');
    if (active && typeof active.scrollIntoView === 'function') {
      active.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
    }

    // Sync data-index on the zoom button so the legacy theme.Photoswipe
    // (which reads evt.currentTarget.dataset.index) opens at the correct
    // image. Legacy index is 1-based among the visible images.
    if (this.zoomBtn) {
      this.zoomBtn.dataset.index = displayPos;
    }

    this.pauseAllVideos();
  };

  CinGallery.prototype.pauseAllVideos = function () {
    var videos = this.root.querySelectorAll('video');
    videos.forEach(function (v) { if (!v.paused) try { v.pause(); } catch (e) {} });
  };

  CinGallery.prototype.bindThumbs = function () {
    var self = this;
    this.thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var idx = parseInt(thumb.dataset.index, 10);
        if (isNaN(idx)) return;
        // BLOCKER #1 fix: pass the absolute slide index (matches the
        // swiper-wrapper position) — Swiper does not skip CSS-hidden
        // slides, so it expects the absolute index. Clicked thumbs are
        // always visible, and so are their paired slides (same set-name).
        self.swiper.slideTo(idx);
      });
    });
  };

  // Clicking the slide image opens the zoom viewer, same as clicking the
  // dedicated zoom button. Delegated on mainEl so it also covers the
  // variant-slot slide and any images swapped in later.
  CinGallery.prototype.bindImageZoom = function () {
    var self = this;
    this.mainEl.addEventListener('click', function (e) {
      if (!self.zoomBtn) return;
      var image = e.target.closest && e.target.closest('.cin-gallery__image');
      if (!image) return;
      self.zoomBtn.click();
    });
  };

  CinGallery.prototype.bindVariantChange = function () {
    var self = this;
    document.addEventListener('variant:change', function (e) {
      var variant = e.detail && e.detail.variant;
      if (!variant) return;

      // Variant-first mode: swap the dedicated slot to the selected variant's
      // image and keep it as the first photo, instead of jumping the gallery
      // to wherever the variant image sits in product.media.
      if (self.variantFirst && self.variantSlot) {
        self.updateVariantSlot(variant);
        if (self.swiper) self.swiper.slideTo(0);
        return;
      }

      var featured = variant.featured_media;
      var setGroup = parseSetGroupFromAlt(featured && featured.alt);
      self.applyImageSetFilter(setGroup);

      if (featured && featured.id) {
        self.slideToMediaId(featured.id);
      }
    });
  };

  CinGallery.prototype.updateVariantSlot = function (variant) {
    var media = variant.featured_media;
    var baseSrc = (media && media.preview_image && media.preview_image.src)
      || (variant.featured_image && variant.featured_image.src);
    if (!baseSrc) return;

    var mediaId = (media && media.id) || '';
    var altText = (media && media.alt) || (variant.featured_image && variant.featured_image.alt) || '';
    if (altText && altText.indexOf('#') !== -1) altText = altText.split('#')[0];

    // Main slide image — preload the new image off-DOM and only swap the
    // visible src once it has decoded, so the slide never blanks. A blank
    // gap during the swap can read as a page flash/refresh on variant change.
    var slideImg = this.variantSlot.querySelector('img');
    if (slideImg) {
      var fullSrc = withWidth(baseSrc, 1440);
      slideImg.classList.remove('lazyload', 'lazyloaded');
      slideImg.removeAttribute('data-src');
      slideImg.removeAttribute('data-srcset');
      slideImg.removeAttribute('srcset');
      var applySrc = function () {
        slideImg.src = fullSrc;
        slideImg.alt = altText;
      };
      var pre = new Image();
      pre.onload = applySrc;
      pre.onerror = applySrc;
      pre.src = fullSrc;
    }
    if (mediaId) this.variantSlot.dataset.mediaId = mediaId;

    // Thumbnail image
    if (this.variantSlotThumb) {
      var thumbImg = this.variantSlotThumb.querySelector('img');
      if (thumbImg) {
        thumbImg.classList.remove('lazyload', 'lazyloaded');
        thumbImg.removeAttribute('data-src');
        thumbImg.removeAttribute('data-srcset');
        thumbImg.removeAttribute('srcset');
        thumbImg.src = withWidth(baseSrc, 240);
      }
      if (mediaId) this.variantSlotThumb.dataset.mediaId = mediaId;
    }

    // PhotoSwipe source entry (keeps zoom aligned with the visible first photo)
    if (this.variantSlotPswp) {
      var zoom = withWidth(baseSrc, 2048);
      this.variantSlotPswp.setAttribute('href', zoom);
      this.variantSlotPswp.setAttribute('data-photoswipe-src', zoom);
      if (media && media.preview_image) {
        if (media.preview_image.width) this.variantSlotPswp.setAttribute('data-photoswipe-width', media.preview_image.width);
        if (media.preview_image.height) this.variantSlotPswp.setAttribute('data-photoswipe-height', media.preview_image.height);
      }
    }
  };

  CinGallery.prototype.applyImageSetFilter = function (groupName) {
    var slides = this.mainEl.querySelectorAll('.swiper-slide');
    slides.forEach(function (s) {
      var setName = s.dataset.setName || '';
      var matches = !groupName || !setName || setName === groupName;
      s.classList.toggle('is-hidden', !matches);
    });
    this.thumbs.forEach(function (t) {
      var setName = t.dataset.setName || '';
      var matches = !groupName || !setName || setName === groupName;
      t.classList.toggle('is-hidden', !matches);
    });
    if (this.swiper) {
      this.swiper.update();
      // Defensive: if the currently active slide just became hidden
      // (e.g. variant change while looking at a non-matching slide),
      // advance to the first visible slide so the viewport isn't blank.
      var activeIdx = this.swiper.realIndex;
      if (slides[activeIdx] && slides[activeIdx].classList.contains('is-hidden')) {
        for (var i = 0; i < slides.length; i++) {
          if (!slides[i].classList.contains('is-hidden')) {
            this.swiper.slideTo(i);
            break;
          }
        }
      }
    }
    this.updateCounterTotal();
  };

  CinGallery.prototype.updateCounterTotal = function () {
    if (!this.counterTotal) return;
    var slides = this.mainEl.querySelectorAll('.swiper-slide');
    var visible = 0;
    slides.forEach(function (s) {
      if (!s.classList.contains('is-hidden')) visible++;
    });
    this.counterTotal.textContent = visible;
  };

  CinGallery.prototype.slideToMediaId = function (mediaId) {
    if (!this.swiper) return;
    var slides = this.mainEl.querySelectorAll('.swiper-slide');
    for (var i = 0; i < slides.length; i++) {
      // BLOCKER #1 fix: pass absolute index `i` (wrapper position),
      // not a count of visible slides.
      if (String(slides[i].dataset.mediaId) === String(mediaId)) {
        this.swiper.slideTo(i);
        return;
      }
    }
  };

  function initAll() {
    var roots = document.querySelectorAll('[data-cin-gallery]');
    roots.forEach(function (el) {
      if (el.__cinGallery) return;
      el.__cinGallery = new CinGallery(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  window.CinGallery = CinGallery;
})();
